import { PaletteColor, Stack, TypeBackground, TypeText } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Theme } from "@mui/material/styles";
import { isObject } from "lodash-es";
import { ColorPartial, CommonColors } from "node_modules/@mui/material/esm/styles/createPalette";

import InlineCode from "@/common/components/Code/InlineCode";
import { THEMES } from "@/common/constants/common";
import { TypeHelper } from "@/common/helpers/type";
import { createCustomTheme } from "@/theme";

type ColorPartialKey = keyof ColorPartial;
const colorPartialKeysAsNumbers: Array<ColorPartialKey> = [
  50, 100, 200, 300, 400, 500, 600, 700, 800, 900,
];
const colorPartialKeysAsStrings: Array<string> = colorPartialKeysAsNumbers.map((x) => x.toString());

interface ColorInfoLocal {
  name: string;
  token: string;
  value: string | undefined;
}

function ColorCard({ colorInfo }: { colorInfo: ColorInfoLocal }) {
  return (
    <Stack direction='row' spacing={1} sx={{ width: 250 }}>
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: 1,
          border: "1px solid grey",
          backgroundColor: colorInfo.value,
        }}
      ></Box>
      <Box>
        <Typography component='div' variant='body1'>
          {colorInfo.name}.{colorInfo.token}
        </Typography>

        <Typography component='div' variant='body1'>
          <InlineCode>{colorInfo.value || "-"}</InlineCode>
        </Typography>
      </Box>
    </Stack>
  );
}

export function DevPalette() {
  const isCommonColor = (colorObj: any) => (colorObj as CommonColors).white;
  const isPaletteColor = (colorObj: any) => (colorObj as PaletteColor).main;
  const isBackgroundColor = (colorObj: any) =>
    (colorObj as Partial<TypeBackground>).default && (colorObj as Partial<TypeBackground>).paper;
  const isTextColor = (colorObj: any) =>
    (colorObj as Partial<TypeText>).primary && (colorObj as Partial<TypeText>).secondary;
  const isPartialColor = (colorObj: any) =>
    Object.keys(colorObj as ColorPartial).length !== 0 &&
    Object.keys(colorObj as ColorPartial).every(
      (key) =>
        colorPartialKeysAsStrings.includes(key.toString()) ||
        colorPartialKeysAsNumbers.includes(Number(key) as ColorPartialKey) ||
        /^A\d+$/.test(key),
    );
  const isDividerColor = (colorName: string, colorObj: any) =>
    colorName === "divider" && TypeHelper.isString(colorObj);

  const createThemeByMode = (mode: THEMES) => {
    const themeByMode = createCustomTheme({
      theme: mode,
      responsiveFontSizes: false,
    });
    return themeByMode;
  };

  const getColorsByThemeMode = (mode: THEMES, themeByMode: Theme) => {
    return Object.entries(themeByMode.palette)
      .filter(
        ([colorName, colorObj]) =>
          (isObject(colorObj) &&
            (isCommonColor(colorObj) ||
              isTextColor(colorObj) ||
              isPaletteColor(colorObj) ||
              isBackgroundColor(colorObj) ||
              isPartialColor(colorObj))) ||
          isDividerColor(colorName, colorObj),
      )
      .map(([colorName, colorObj]) => ({
        name: colorName,
        value: colorObj as PaletteColor,
        commonColor: isCommonColor(colorObj) ? (colorObj as CommonColors) : undefined,
        paletteColor: isPaletteColor(colorObj) ? (colorObj as PaletteColor) : undefined,
        backgroundColor: isBackgroundColor(colorObj)
          ? (colorObj as Partial<TypeBackground>)
          : undefined,
        textColor: isTextColor(colorObj) ? (colorObj as Partial<TypeText>) : undefined,
        partialColor: isPartialColor(colorObj) ? (colorObj as ColorPartial) : undefined,
        dividerColor: isDividerColor(colorName, colorObj) ? (colorObj as string) : undefined,
      }))
      .map((color) => ({
        ...color,
        variants:
          (color.commonColor && [
            { name: "white", value: color.commonColor.white },
            { name: "black", value: color.commonColor.black },
          ]) ||
          (color.textColor && [
            { name: "primary", value: color.textColor.primary },
            { name: "secondary", value: color.textColor.secondary },
            { name: "disabled", value: color.textColor.disabled },
            { name: "main", value: color.textColor.main },
            { name: "dark", value: color.textColor.dark },
            { name: "light", value: color.textColor.light },
            { name: "contrastText", value: color.textColor.contrastText },
            { name: "disabledLight", value: color.textColor.disabledLight },
          ]) ||
          (color.paletteColor && [
            { name: "main", value: color.paletteColor.main },
            { name: "dark", value: color.paletteColor.dark },
            { name: "light", value: color.paletteColor.light },
            { name: "contrastText", value: color.paletteColor.contrastText },
          ]) ||
          (color.backgroundColor && [
            { name: "default", value: color.backgroundColor.default },
            { name: "paper", value: color.backgroundColor.paper },
            { name: "gray", value: color.backgroundColor.gray },
          ]) ||
          (color.partialColor &&
            Object.entries(color.partialColor).map(([key, value]) => ({
              name: key,
              value: value,
            }))) ||
          (color.dividerColor && [
            {
              name: "divider",
              value: color.dividerColor,
            },
          ]) ||
          [],
      }));
  };

  return (
    <Stack spacing={2}>
      {Object.values(THEMES).map((mode) => {
        const themeByMode = createThemeByMode(mode);

        return (
          <Stack key={mode} gap={1}>
            <Stack gap={1}>
              <Typography component='div' variant='h4'>
                {`Palette ${mode}`}
              </Typography>

              <Stack spacing={1}>
                {getColorsByThemeMode(mode, themeByMode).map((color, i) => {
                  return (
                    <Box key={i}>
                      <Stack direction='row' gap={1} flexWrap='wrap'>
                        {color.variants.map((variant, j) => (
                          <ColorCard
                            key={j}
                            colorInfo={{
                              name: color.name,
                              token: variant.name,
                              value: variant.value,
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Stack>

            <Stack gap={1}>
              <Typography component='div' variant='h6'>
                action
              </Typography>

              <Stack direction='row' gap={1} flexWrap='wrap'>
                {[
                  { token: "active", value: themeByMode.palette.action.active },
                  { token: "disabled", value: themeByMode.palette.action.disabled },
                  {
                    token: "disabledBackground",
                    value: themeByMode.palette.action.disabledBackground,
                  },
                  { token: "focus", value: themeByMode.palette.action.focus },
                  { token: "hover", value: themeByMode.palette.action.hover },
                  { token: "selected", value: themeByMode.palette.action.selected },
                ].map((variant, j) => (
                  <ColorCard
                    key={j}
                    colorInfo={{
                      name: "action",
                      token: variant.token,
                      value: variant.value,
                    }}
                  />
                ))}
              </Stack>

              <Stack>
                {[
                  { name: "activatedOpacity", value: themeByMode.palette.action.activatedOpacity },
                  { name: "disabledOpacity", value: themeByMode.palette.action.disabledOpacity },
                  {
                    name: "focusOpacity",
                    value: themeByMode.palette.action.focusOpacity,
                  },
                  { name: "hoverOpacity", value: themeByMode.palette.action.hoverOpacity },
                  { name: "selectedOpacity", value: themeByMode.palette.action.selectedOpacity },
                ].map((item, z) => (
                  <Box key={z} sx={{ width: 200 }}>
                    {item.name}: <InlineCode>{item.value}</InlineCode>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
}
